import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { BdsBanner, BdsSelect, BdsTableCell, BdsTableRow, BdsTypo } from 'blip-ds/dist/blip-ds-react';
import SessionConsumptionContext from '../../../../contexts/SessionConsumptionContext';
import { PageHeaderComponent } from '../../components/Metrics/PageHeader/PageHeaderComponent';
import { trackEvent } from '../../../../api/Billing/Tracking';
import { getMonthTwoDigits } from '../../../../utils/date';
import '../../../../typings/blip-ds.d';
import {
  SessionConsumptionRow,
  SessionConsumptionPaper,
  SessionConsumptionHeaderFilter,
} from '../styles';
import {
  action,
  origin,
  REPORT_PERIOD_SELECT,
  REPORT_SESSION_CONSUMPTION_CONVERSATION_SELECT,
  REPORT_SESSION_CONSUMPTION_WABAID_SELECT,
  REPORT_SESSION_CONSUMPTION_PER_PHONENUMBER_BUSINESSINITIATED_TAB_CLICK,
  REPORT_SESSION_CONSUMPTION_PER_PHONENUMBER_USERINITIATED_TAB_CLICK,
} from '../../../../shared/SegmentEvents';
import FeatureToggleService from '../../../../services/FeatureToggleService';
import { BillingMetrics } from '../../../../shared/BillingMetrics';
import TableWithPagination from '../../../../components/TableWithPagination/TableWithPagination';
import { useSortedPaginatedData } from '../../../../hooks/useSortedPaginatedData';
import { DEFAULT_PAGE_OPTIONS, COLUMN_WIDTHS_7 as BUSINESS_COLUMN_WIDTHS, COLUMN_WIDTHS_3 as USER_COLUMN_WIDTHS } from '../../../../utils/paginationUtils';

const BUSINESS_COLUMN_KEYS = ['phoneNumber', 'utility', 'authentication', 'marketing', 'marketing_lite', 'service', 'total'];
const USER_COLUMN_KEYS = ['phoneNumber', 'free', 'total'];
const OLD_COLUMN_KEYS = ['phoneNumber', 'sessionByUser', 'sessionByCompany', 'sessionTotal'];
const OLD_COLUMN_WIDTHS = USER_COLUMN_WIDTHS;

export interface SessionConsumptionMonthlyUsageProps {
  isDetailedView?: boolean;
}

const SessionConsumptionMonthlyUsageComponent: FC<SessionConsumptionMonthlyUsageProps> = ({
  isDetailedView = false,
}) => {
  const {
    dateToSearch,
    content,
    tenant,
    loggedUser,
    trackingProps,
    getPlanStatus,
    billingSessionConsumptionApi,
    oldSessionConsumptionMonthlyUsage,
    setOldSessionConsumptionMonthlyUsage,
    periodOptions,
    setDateToSearch,
    conversationTypeFilter,
    conversationWabaFilter,
    conversationTypeFilterOptions,
    conversationWabaFilterOptions,
    setConversationTypeFilter,
    setConversationWabaFilter,
    isWabaloading,
    WHATSAPP_BP_PRICING_CHANGES_START_DATE,
    showSessionNewPricing,
    setShowSessionNewPricing,
  } = useContext(SessionConsumptionContext);
  const [isLoading, setLoading] = useState(false);
  const [businessInitiatedMonthlyUsage, setBusinessInitiatedMonthlyUsage] = useState([] as any);
  const [userInitiatedMonthlyUsage, setUserInitiatedMonthlyUsage] = useState([] as any);

  const {
    itemsPerPage: businessItemsPerPage,
    sortConfig: businessSortConfig,
    sortedData: sortedBusinessData,
    paginatedData: paginatedBusinessData,
    handlePaginationChange: handleBusinessPaginationChange,
    handleItemsPerPageChange: handleBusinessItemsPerPageChange,
    handleSortChange: handleBusinessSortChange,
  } = useSortedPaginatedData({ data: businessInitiatedMonthlyUsage });

  const {
    itemsPerPage: userItemsPerPage,
    sortConfig: userSortConfig,
    sortedData: sortedUserData,
    paginatedData: paginatedUserData,
    handlePaginationChange: handleUserPaginationChange,
    handleItemsPerPageChange: handleUserItemsPerPageChange,
    handleSortChange: handleUserSortChange,
  } = useSortedPaginatedData({ data: userInitiatedMonthlyUsage });

  const {
    itemsPerPage: oldItemsPerPage,
    sortConfig: oldSortConfig,
    sortedData: sortedOldData,
    paginatedData: paginatedOldData,
    handlePaginationChange: handleOldPaginationChange,
    handleItemsPerPageChange: handleOldItemsPerPageChange,
    handleSortChange: handleOldSortChange,
  } = useSortedPaginatedData({ data: oldSessionConsumptionMonthlyUsage });

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        const monthString = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
        const showNewPricing = dateToSearch >= WHATSAPP_BP_PRICING_CHANGES_START_DATE;
        const storageType =
          (await FeatureToggleService.isDatabricksStorageTypeEnabled(BillingMetrics.session)) ? 1 : 0;

        setShowSessionNewPricing(showNewPricing);
        if (showNewPricing) {
          const data = await billingSessionConsumptionApi.getMonthlySessionConsumption(
            tenant.info.id,
            monthString,
            -1,
            conversationWabaFilter,
            storageType,
          );
          setBusinessInitiatedMonthlyUsage(
            data.map((obj) => ({
              phoneNumber: obj.phoneNumber,
              utility: obj.businessInitiated.utility,
              authentication: obj.businessInitiated.authentication,
              marketing: obj.businessInitiated.marketing,
              marketing_lite: obj.businessInitiated.marketing_lite,
              service: obj.businessInitiated.service,
              total: obj.businessInitiated.total,
            })),
          );
          setUserInitiatedMonthlyUsage(
            data.map((obj) => ({
              phoneNumber: obj.phoneNumber,
              free: obj.userInitiated.free,
              total: obj.userInitiated.total,
            })),
          );
        } else {
          const oldData = await billingSessionConsumptionApi.getOldMonthlySessionConsumption(
            tenant.info.id,
            monthString,
            -1,
            conversationTypeFilter,
            conversationWabaFilter,
          );
          setOldSessionConsumptionMonthlyUsage(oldData);
        }
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [dateToSearch, conversationTypeFilter, conversationWabaFilter]);

  const businessRows = useMemo(
    () =>
      paginatedBusinessData.length > 0
        ? paginatedBusinessData.map((item) => (
            <BdsTableRow key={item.phoneNumber}>
              <BdsTableCell>
                <BdsTypo variant="fs-14" bold="bold" margin={false}>
                  {item.phoneNumber}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.utility}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.authentication}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.marketing}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.marketing_lite}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.service}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.total}
                </BdsTypo>
              </BdsTableCell>
            </BdsTableRow>
          ))
        : undefined,
    [paginatedBusinessData],
  );

  const userRows = useMemo(
    () =>
      paginatedUserData.length > 0
        ? paginatedUserData.map((item) => (
            <BdsTableRow key={item.phoneNumber}>
              <BdsTableCell>
                <BdsTypo variant="fs-14" bold="bold" margin={false}>
                  {item.phoneNumber}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.free}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.total}
                </BdsTypo>
              </BdsTableCell>
            </BdsTableRow>
          ))
        : undefined,
    [paginatedUserData],
  );

  const oldRows = useMemo(
    () =>
      paginatedOldData.length > 0
        ? paginatedOldData.map((item) => (
            <BdsTableRow key={item.phoneNumber}>
              <BdsTableCell>
                <BdsTypo variant="fs-14" bold="bold" margin={false}>
                  {item.phoneNumber}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.sessionByUser}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.sessionByCompany}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.sessionTotal}
                </BdsTypo>
              </BdsTableCell>
            </BdsTableRow>
          ))
        : undefined,
    [paginatedOldData],
  );

  const periodOptionChangeEvent = async (selectedOption) => {
    const dateToRetrieveData = new Date(selectedOption.detail.value);
    setDateToSearch(dateToRetrieveData);
    const planStatus = await getPlanStatus();
    trackEvent(REPORT_PERIOD_SELECT, tenant, loggedUser, {
      ...trackingProps,
      date: dateToRetrieveData,
      planStatus,
      origin: origin.SESSIONPERPHONENUMBER,
    });
  };

  const wabaOptionChangeEvent = (selectedOption) => {
    const conversationWabaValue = selectedOption.detail.value;
    setConversationWabaFilter(conversationWabaValue);
    trackEvent(REPORT_SESSION_CONSUMPTION_WABAID_SELECT, tenant, loggedUser, {
      ...trackingProps,
      whatsappConversationWaba: conversationWabaValue,
      origin: origin.SESSIONPERPHONENUMBER,
    });
  };

  const typeOptionChangeEvent = (selectedOption) => {
    const conversationTypeValue = selectedOption.detail.value;
    setConversationTypeFilter(conversationTypeValue);
    trackEvent(REPORT_SESSION_CONSUMPTION_CONVERSATION_SELECT, tenant, loggedUser, {
      ...trackingProps,
      whatsappConversationType: conversationTypeValue,
      origin: origin.SESSIONPERPHONENUMBER,
    });
  };

  return (
    <>
      {isDetailedView && (
        <PageHeaderComponent
          title={content.chatbotSessionConsumption.title}
          dateToSearch={dateToSearch}
          periodOptions={periodOptions}
          periodOptionChangeEvent={periodOptionChangeEvent}
        />
      )}
      <SessionConsumptionPaper>
        <SessionConsumptionRow isDetailedView={isDetailedView}>
          {!isDetailedView && (
            <BdsTypo variant="fs-24" bold="bold" margin={false}>
              {content.chatbotSessionConsumption.title}
            </BdsTypo>
          )}
          <BdsTypo variant="fs-14" margin={false}>
            {content.chatbotSessionConsumption.about}
          </BdsTypo>
          {isDetailedView && (
            <SessionConsumptionHeaderFilter>
              {conversationWabaFilterOptions.length > 0 && (
                <BdsSelect
                  disabled={conversationWabaFilterOptions.length === 1}
                  value={conversationWabaFilter}
                  label={content.conversationWabaLabel}
                  onBdsChange={wabaOptionChangeEvent}
                >
                  {conversationWabaFilterOptions}
                </BdsSelect>
              )}
              {!showSessionNewPricing && (
                <BdsSelect
                  value={conversationTypeFilter}
                  label={content.conversationTypeLabel}
                  onBdsChange={typeOptionChangeEvent}
                >
                  {conversationTypeFilterOptions}
                </BdsSelect>
              )}
            </SessionConsumptionHeaderFilter>
          )}
        </SessionConsumptionRow>
        {showSessionNewPricing ? (
          <bds-tab-group
            align="left"
            content-scrollable="false"
            onClick={(e) => {
              try {
                const isActiveFirstTab = e.target.firstChild.getAttribute('open') === '';
                if (isActiveFirstTab) {
                  trackEvent(REPORT_SESSION_CONSUMPTION_PER_PHONENUMBER_USERINITIATED_TAB_CLICK, tenant, loggedUser, {
                    ...trackingProps,
                    action: action.CLICK,
                    origin: origin.SESSIONPERPHONENUMBER,
                  });
                } else {
                  trackEvent(
                    REPORT_SESSION_CONSUMPTION_PER_PHONENUMBER_BUSINESSINITIATED_TAB_CLICK,
                    tenant,
                    loggedUser,
                    { ...trackingProps, action: action.CLICK, origin: origin.SESSIONPERPHONENUMBER },
                  );
                }
              } catch (error) {}
            }}
          >
            <bds-tab-item label={content.sessionConsumptionByPhone.businessInitiated.tab}>
              <BdsBanner variant="system" context="inside">
                {content.sessionConsumptionByPhone.businessInitiated.bannerText}
              </BdsBanner>
              <TableWithPagination
                options={{
                  heading: [
                    content.sessionConsumptionByPhone.columnPhoneNumber,
                    content.sessionConsumptionByPhone.businessInitiated.columnUtility,
                    content.sessionConsumptionByPhone.businessInitiated.columnAuthentication,
                    content.sessionConsumptionByPhone.businessInitiated.columnMarketing,
                    content.sessionConsumptionByPhone.businessInitiated.columnMarketingLite,
                    content.sessionConsumptionByPhone.businessInitiated.columnService,
                    content.sessionConsumptionByPhone.columnTotal,
                  ],
                  columnKeys: BUSINESS_COLUMN_KEYS,
                  columnWidths: BUSINESS_COLUMN_WIDTHS,
                  sortConfig: businessSortConfig,
                  onSortChange: handleBusinessSortChange,
                  itemsPerPageList: DEFAULT_PAGE_OPTIONS,
                  itemsPerPage: businessItemsPerPage,
                  numberOfItems: isLoading || isWabaloading ? businessItemsPerPage : sortedBusinessData.length,
                  isLoading: isLoading || isWabaloading,
                  hasEmptyState: !(isLoading || isWabaloading) && sortedBusinessData.length === 0,
                  emptyStateSubtitle: '',
                  isAutoHeight: true,
                }}
                onPaginationChange={handleBusinessPaginationChange}
                onItemsPerPageChange={handleBusinessItemsPerPageChange}
              >
                {businessRows}
              </TableWithPagination>
            </bds-tab-item>
            <bds-tab-item label={content.sessionConsumptionByPhone.userInitiated.tab}>
              <bds-grid margin="t-3">
                <TableWithPagination
                  options={{
                    heading: [
                      content.sessionConsumptionByPhone.columnPhoneNumber,
                      content.sessionConsumptionByPhone.userInitiated.free,
                      content.sessionConsumptionByPhone.columnTotal,
                    ],
                    columnKeys: USER_COLUMN_KEYS,
                    columnWidths: USER_COLUMN_WIDTHS,
                    sortConfig: userSortConfig,
                    onSortChange: handleUserSortChange,
                    itemsPerPageList: DEFAULT_PAGE_OPTIONS,
                    itemsPerPage: userItemsPerPage,
                    numberOfItems: isLoading || isWabaloading ? userItemsPerPage : sortedUserData.length,
                    isLoading: isLoading || isWabaloading,
                    hasEmptyState: !(isLoading || isWabaloading) && sortedUserData.length === 0,
                    emptyStateSubtitle: '',
                    isAutoHeight: true,
                  }}
                  onPaginationChange={handleUserPaginationChange}
                  onItemsPerPageChange={handleUserItemsPerPageChange}
                >
                  {userRows}
                </TableWithPagination>
              </bds-grid>
            </bds-tab-item>
          </bds-tab-group>
        ) : (
          <TableWithPagination
            options={{
              heading: [
                content.chatbotSessionConsumption.columnPhoneNumber,
                content.chatbotSessionConsumption.columnUserSession,
                content.chatbotSessionConsumption.columnEnterpriseSession,
                content.chatbotSessionConsumption.columnTotal,
              ],
              columnKeys: OLD_COLUMN_KEYS,
              columnWidths: OLD_COLUMN_WIDTHS,
              sortConfig: oldSortConfig,
              onSortChange: handleOldSortChange,
              itemsPerPageList: DEFAULT_PAGE_OPTIONS,
              itemsPerPage: oldItemsPerPage,
              numberOfItems: isLoading || isWabaloading ? oldItemsPerPage : sortedOldData.length,
              isLoading: isLoading || isWabaloading,
              hasEmptyState: !(isLoading || isWabaloading) && sortedOldData.length === 0,
              emptyStateSubtitle: '',
              isAutoHeight: true,
            }}
            onPaginationChange={handleOldPaginationChange}
            onItemsPerPageChange={handleOldItemsPerPageChange}
          >
            {oldRows}
          </TableWithPagination>
        )}
      </SessionConsumptionPaper>
    </>
  );
};

export default SessionConsumptionMonthlyUsageComponent;
